import { useState } from "react";
import { MapPin, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

export const Visit = () => {
  const [form, setForm] = useState({ name: "", email: "", date: "", guests: 2, notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/visit/reserve", { ...form, guests: Number(form.guests) });
      toast.success("Tasting request received — we'll confirm by email.");
      setForm({ name: "", email: "", date: "", guests: 2, notes: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors";

  return (
    <section id="visit" data-testid="visit-section" className="relative py-28 lg:py-40 bg-[#141210]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <SectionHeading overline="Taste With Us" title="A seat at the cellar bar." />
          <Reveal delay={0.2}>
            <p className="text-base text-[#A39E93] leading-relaxed mt-6 max-w-md">
              Reserve a seated tasting or join a seasonal event — walk-ins welcome when space allows.
              Groups of eight or more: email ahead so we can staff the bar.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <Clock size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E685E]">Hours</p>
                  <p className="text-sm text-[#F4F1EA] mt-1">Thursday – Sunday · 12pm – 6pm</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E685E]">Find Us</p>
                  <p className="text-sm text-[#F4F1EA] mt-1">California Delta · admin@deltabreezewinery.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="walkin-status">
                <Users size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E685E]">Today</p>
                  <p className="text-sm text-[#F4F1EA] mt-1 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] inline-block" />
                    Walk-ins welcome
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            data-testid="visit-reservation-form"
            onSubmit={submit}
            className="bg-[#0A0908] border border-[rgba(212,175,55,0.2)] p-8 lg:p-10 flex flex-col gap-4"
          >
            <p className="overline-label mb-2">Reserve a Tasting</p>
            <input
              data-testid="visit-input-name"
              required
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="visit-input-email"
              required
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                data-testid="visit-input-date"
                required
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={`${inputCls} [color-scheme:dark]`}
              />
              <input
                data-testid="visit-input-guests"
                required
                type="number"
                min="1"
                max="20"
                placeholder="Guests"
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
                className={inputCls}
              />
            </div>
            <textarea
              data-testid="visit-input-notes"
              placeholder="Occasion, preferences, anything we should know (optional)"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={`${inputCls} resize-none`}
            />
            {error && <p data-testid="visit-form-error" className="text-sm text-[#C86D51]">{error}</p>}
            <button
              data-testid="visit-submit-btn"
              disabled={busy}
              className="mt-2 font-mono text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] py-4 hover:bg-[#F4F1EA] transition-colors disabled:opacity-50"
            >
              {busy ? "Requesting…" : "Request Reservation"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
